import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IdeaTile } from '@/features/inspiration/components/IdeaTile';
import type { IdeaCardData } from '@/features/inspiration/data/story-ideas';

/**
 * SI-Inspiration follow-up 2026-05-11 (Sebastian Gate-1 post-verify).
 *
 * Conditional expander for the Story-Ideen vertical stack. Mirrors
 * TipsExpander's UX — keeps the top of the Story-Ideen section
 * scannable while still surfacing the full content on demand.
 *
 *   - cards.length <= topNVisible → render all cards, NO toggle
 *     (clean exit; no UI clutter when there's nothing to reveal)
 *   - cards.length >  topNVisible → render first `topNVisible`
 *     cards + a toggle that reveals the remaining ones inline
 *
 * Toggle text + icon switches between collapsed/expanded states.
 * aria-expanded plumbed for screen readers. Initial state is
 * collapsed when the expander is present.
 *
 * Expander state resets across world-filter changes by parent
 * remount (InspirationRoute keys this component on the selected
 * world value — natural React-tree reset).
 */
export interface IdeaCardsExpanderProps {
  cards: readonly IdeaCardData[];
  /** Number of cards visible before the toggle reveals the rest. Default 5. */
  topNVisible?: number;
  className?: string;
  testId?: string;
}

export function IdeaCardsExpander({
  cards,
  topNVisible = 5,
  className,
  testId,
}: IdeaCardsExpanderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const needsExpander = cards.length > topNVisible;
  const visibleCards = needsExpander ? cards.slice(0, topNVisible) : cards;
  const hiddenCards = needsExpander ? cards.slice(topNVisible) : [];

  return (
    <div className={cn('flex w-full flex-col gap-3', className)} data-testid={testId}>
      {visibleCards.map((idea) => (
        <IdeaTile
          key={idea.id}
          emoji={idea.emoji}
          name={idea.name}
          subtitle={idea.subtitle}
          world={idea.world}
          testId={`idea-tile-${idea.id}`}
        />
      ))}

      {needsExpander && (
        <div className="flex w-full flex-col rounded-lg border border-dream-gold bg-dream-white">
          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            aria-expanded={isOpen}
            aria-controls={testId ? `${testId}-hidden-list` : undefined}
            className="flex w-full items-center justify-between gap-2 p-4 text-left transition-colors hover:bg-dream-background focus-visible:outline-2 focus-visible:outline-dream-navy/50"
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            <span className="text-sm font-semibold text-dream-navy">
              {isOpen ? 'Weniger anzeigen' : '💡 Weitere Geschichten-Ideen anzeigen'}
            </span>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 shrink-0 text-dream-navy" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0 text-dream-navy" aria-hidden />
            )}
          </button>

          {isOpen && (
            <div
              id={testId ? `${testId}-hidden-list` : undefined}
              className="flex w-full flex-col gap-3 border-t border-dream-gold/40 p-4"
              data-testid={testId ? `${testId}-hidden-list-content` : undefined}
            >
              {hiddenCards.map((idea) => (
                <IdeaTile
                  key={idea.id}
                  emoji={idea.emoji}
                  name={idea.name}
                  subtitle={idea.subtitle}
                  world={idea.world}
                  testId={`idea-tile-${idea.id}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
